import { Injectable, OnDestroy } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute } from '@angular/router';
import {
  ActiveTestQueuedStateService,
  ApiService,
  Device,
  DialogService,
  EntityCacheProvider,
  FormatService,
  LmapService,
  RunTypeValue,
  TestAssembleService,
  TestServerConfigService,
  TransformationService
} from '@nrwl/common-lib';
import { BaseSetupService, BaseSetupTab, setupConfig } from '@nrwl/test-setup';
import { Subject } from 'rxjs';
import { TwampReflectorConfigMapping as ConfigFieldMapping } from './model/twamp-reflector-agent-model';
@Injectable({
  providedIn: 'root'
})
export class TwampReflectorTestService extends BaseSetupService implements OnDestroy {
  state: ActivatedRoute;
  exceedsIPRange: boolean;
  entityCache: EntityCacheProvider;
  transformService: TransformationService;
  formatService: FormatService;
  lmap: LmapService;
  activeTestQueuedStateService: ActiveTestQueuedStateService;
  serverConfigService: TestServerConfigService;
  deviceModel = 'vPMA';
  testTaskName = 'twamp-reflector';
  selectedOriginator: Device;
  selectedDestination: any;
  testAssembleService: TestAssembleService;
  tabDataChangeEvents = new Subject<number>();
  attributeMapping = {
    sectionName: 'otherOptions',
    twampUDPPort: 'no_filter_twamp_udp_port',
    twampEnabled: 'no_filter_twamp_enabled',
    twampIgnoreIp: 'no_filter_twamp_ignore_ip'
  };
  private readonly unsubscribe: Subject<void> = new Subject();
  constructor(
    apiService: ApiService,
    state: ActivatedRoute,
    dialogService: DialogService,
    dialog: MatDialog,
    entityCache: EntityCacheProvider,
    transformService: TransformationService,
    testAssembleService: TestAssembleService,
    lmapService: LmapService,
    formatService: FormatService,
    serverConfigService: TestServerConfigService
  ) {
    super(apiService, dialogService, dialog);
    this.state = state;
    this.tabClass = BaseSetupTab;
    this.entityCache = entityCache;
    this.transformService = transformService;
    this.formatService = formatService;
    this.lmap = lmapService;
    this.serverConfigService = serverConfigService;
    this.testAssembleService = testAssembleService;
  }

  openTestSetup(
    setupId: string,
    setupConfig: setupConfig,
    defaultParamsById = {},
    onSuccess = () => {},
    onFail = () => {},
    //eslint-disable-next-line
    agentBasedConfig: any
  ): void {
    super.openTestSetup(
      setupId,
      setupConfig,
      defaultParamsById,
      onSuccess,
      onFail,
      agentBasedConfig
    );
  }

  ngOnDestroy(): void {
    this.unsubscribe.unsubscribe();
  }

  toggleTabChange(index: number): void {
    super.toggleTabChange(index);
    this.tabDataChangeEvents.next(index);
  }

  setOriginatorAgent(device: Device): void {
    this.selectedOriginator = device;
  }

  createLMAPObject(): any {
    if (this.output[ConfigFieldMapping.AGENT_TYPE]?.includes('JMEP')) {
      return this.getJmepTestConfig();
    } else {
      return this.getTestConfig();
    }
  }

  getTemplateConfig(): any {
    if (this.output[ConfigFieldMapping.AGENT_TYPE]?.includes('JMEP')) {
      return {
        udpPort: this.output[ConfigFieldMapping.UDP_PORT].toString()
      };
    } else {
      return this.getTestOption();
    }
  }

  getTestConfig(): any {
    const serverConfig = this.serverConfigService.getCollectorConfig();

    const collectorURL = this.lmap.buildCollectorPostUrl(
      serverConfig,
      this.output[ConfigFieldMapping.AGENT_TYPE]
    );
    if (collectorURL == null) {
      // this.setErrorMessage(
      //   'Collector information is not available. Verify browser has connectivity to server and verify server configuration.'
      // );
      return null;
    }

    // create the test schedule
    // create task name and testschedulename based on modes
    const testScheduleName = this.output[ConfigFieldMapping.ALIAS];
    let testTaskName = '';
    if (this.output[ConfigFieldMapping.REFLECTOR_MODE] == 'full') {
      testTaskName = 'twamp-reflector';
    } else {
      testTaskName = 'twamp-light-reflector';
    }

    const testActionName = testScheduleName + '-action';

    const testEventName = 'immediate';

    const optionArray = this.getTestOption();
    const action = this.lmap.createAction(testActionName, testTaskName, optionArray);
    const actionArray = [];
    actionArray.push(action);
    // while report schedule references could be created here they will be added automatically to this schedule via addResultScheduleAndEvent()

    const testSchedule = this.lmap.createSchedule(testScheduleName, testEventName, actionArray);

    // build the LMAP configuration (test schedule + result schedules + all events other than those predefined by MA such as immediate)
    const lmapified = this.lmap.addSchedule(testSchedule);

    // results are not posted but define one result schedule for now so test controller can
    // identify this as a test schedule.
    //lmapified = lmap.addResultScheduleAndEvent(lmapified, testScheduleName, "periodic", "rt", collectorURL, 60);

    const tpInfo = {
      name: this.output[ConfigFieldMapping.SELECTED_ORIGINATOR].deviceData.name,
      nameId: this.output[ConfigFieldMapping.SELECTED_ORIGINATOR].deviceData.nameId,
      type: 'inventory'
    };
    const controllerConfig = this.testAssembleService.buildPrimaryTestConfig(
      tpInfo,
      testTaskName,
      lmapified
    );
    controllerConfig.test['test-list-data'] = {};
    controllerConfig.test['test-list-data']['runType'] = RunTypeValue.ON_GOING;
    // controllerConfig.test['test-list-data'].instanceName = this.formObj.instanceName;
    controllerConfig.test['ipv6'] =
      this.output[ConfigFieldMapping.IP_VERSION] == 'v6' ? 'yes' : 'no';
    return controllerConfig;
  }

  getTestOption(): any {
    const twampServer = {};
    twampServer['server-admin-state'] = true; //purposely hard-coded - this is all we support
    twampServer['server-tcp-port'] = this.output[ConfigFieldMapping.CONTROL_PORT]?.toString();
    twampServer['dscp'] = 0; //purposely hard-coded - this is all we support
    twampServer['mode'] = 'bits.unauthenticated'; //purposely hard-coded - this is all we support

    // ------ Wrap the test-specific configuration in LMAP
    const twampTest = {};
    twampTest['twamp-server'] = twampServer;

    const allTestParams = {};
    allTestParams['ietf-twamp:twamp'] = twampTest;
    const isIpv6 = 'v6' === this.output[ConfigFieldMapping.IP_VERSION];
    const actionOption2 = {
      id: '',
      value: ''
    };
    if (isIpv6) {
      actionOption2.id = 'prefix-length';
      actionOption2.value = this.output[ConfigFieldMapping.PREFIX_V6];
    } else {
      actionOption2.id = 'subnet';
      actionOption2.value = this.output[ConfigFieldMapping.SUBNET];
    }
    const actionOption3 = {
      id: '',
      value: ''
    };
    actionOption3.id = 'gateway';
    actionOption3.value = isIpv6
      ? this.output[ConfigFieldMapping.GATEWAY_V6].trim()
      : this.output[ConfigFieldMapping.GATEWAY].trim();

    const actionOption4 = {
      id: '',
      value: ''
    };
    actionOption4.id = 'source-ip';
    actionOption4.value = isIpv6
      ? this.output[ConfigFieldMapping.ADDRESS_IPV6].trim()
      : this.output[ConfigFieldMapping.ADDRESS].trim();

    const persist = this.output[ConfigFieldMapping.REBOOT];
    const actionOptionReboot = {
      id: '',
      value: null
    };
    actionOptionReboot.id = 'persist';
    actionOptionReboot.value = persist ? true : false;

    const optionArray = [];
    optionArray.push(actionOption4);
    optionArray.push(actionOption2);
    optionArray.push(actionOption3);
    optionArray.push(actionOptionReboot);

    if (this.output[ConfigFieldMapping.REFLECTOR_MODE] == 'full') {
      const actionOption = {
        id: '',
        value: ''
      };
      actionOption.id = 'twamp';
      actionOption.value = JSON.stringify(allTestParams);

      const actionOption5 = {
        id: '',
        value: ''
      };
      actionOption5.id = 'max-connections';
      actionOption5.value = this.output[ConfigFieldMapping.SESSIONS].toString();

      const actionOption6 = {
        id: '',
        value: ''
      };
      actionOption6.id = 'max-flows';
      actionOption6.value = this.output[ConfigFieldMapping.FLOWS].toString();

      optionArray.push(actionOption6);
      optionArray.push(actionOption5);
      optionArray.push(actionOption);
    } else {
      const actionOption7 = {
        id: '',
        value: ''
      };
      actionOption7.id = 'reflector-udp-port';
      actionOption7.value = this.output[ConfigFieldMapping.UDP_PORT];
      optionArray.push(actionOption7);
    }

    // if our agent has multiple ports
    if (this.output[ConfigFieldMapping.SELECTED_ORIGINATOR].deviceData?.phyId) {
      const actionOptionPhy = {
        id: '',
        value: ''
      };
      actionOptionPhy.id = 'phy-id';
      actionOptionPhy.value = this.output[ConfigFieldMapping.SELECTED_ORIGINATOR].deviceData?.phyId;
      optionArray.push(actionOptionPhy);
    }

    if (
      this.output[ConfigFieldMapping.AGENT_TYPE].indexOf('vPMA') == -1 &&
      this.output[ConfigFieldMapping.VLAN_CONFIG] !== 'untagged' &&
      this.output[ConfigFieldMapping.SVLAN_ID]
    ) {
      //VLAN and Priority
      const actionOptionVlanId = {
        id: '',
        value: ''
      };
      actionOptionVlanId.id = 'vlan-id';
      actionOptionVlanId.value = this.output[ConfigFieldMapping.SVLAN_ID];
      optionArray.push(actionOptionVlanId);

      const actionOptionVlanPriority = {
        id: '',
        value: ''
      };
      actionOptionVlanPriority.id = 'vlan-priority';
      actionOptionVlanPriority.value = '0'; // default value
      optionArray.push(actionOptionVlanPriority);
    }

    if (
      this.output[ConfigFieldMapping.AGENT_TYPE].indexOf('vPMA') != -1 &&
      this.output[ConfigFieldMapping.VLAN_CONFIG] !== 'untagged' &&
      this.output[ConfigFieldMapping.SVLAN_ID]
    ) {
      // Q-in-Q
      const actionOptionVlanId = {
        id: '',
        value: ''
      };
      actionOptionVlanId.id = 'vlan-id';
      actionOptionVlanId.value = this.output[ConfigFieldMapping.SVLAN_ID];
      optionArray.push(actionOptionVlanId);

      const actionOptionVlanPriority = {
        id: '',
        value: ''
      };
      actionOptionVlanPriority.id = 'vlan-priority';
      actionOptionVlanPriority.value = '0'; // default value
      optionArray.push(actionOptionVlanPriority);

      if (this.output[ConfigFieldMapping.VLAN_CONFIG] === 'q_in_q') {
        const actionOptionCvlanId = {
          id: '',
          value: ''
        };
        actionOptionCvlanId.id = 'inner-vlan-id';
        actionOptionCvlanId.value = this.output[ConfigFieldMapping.CVLAN_ID];
        optionArray.push(actionOptionCvlanId);

        const actionOptionCvlanIdPriority = {
          id: '',
          value: ''
        };
        actionOptionCvlanIdPriority.id = 'inner-vlan-priority';
        actionOptionCvlanIdPriority.value = '0'; // default value
        optionArray.push(actionOptionCvlanIdPriority);

        const actionOptionTpid = {
          id: '',
          value: ''
        };
        actionOptionTpid.id = 'tpid';
        actionOptionTpid.value = this.output[ConfigFieldMapping.ETHER_TYPE];
        optionArray.push(actionOptionTpid);
      }
    }
    return optionArray;
  }
  //eslint-disable-next-line
  getJmepTestConfig(): any {
    const subAttributeArray = [];
    const lmapOptionsArray = {
      'config-attributes': {
        name: 'probe-attributes',
        'reflector-schedule-name': this.output[ConfigFieldMapping.ALIAS],
        option: [
          {
            id: this.output[ConfigFieldMapping.SELECTED_ORIGINATOR].deviceData?.capabilities.phy[
              'jmep-id'
            ],
            name: this.output[ConfigFieldMapping.SELECTED_ORIGINATOR].deviceData?.capabilities
              .system['hostname'],
            value: {
              data: []
            }
          }
        ]
      }
    };
    const subAttribute1 = {
      id: '',
      value: ''
    };
    subAttribute1.id = this.attributeMapping.twampEnabled;
    subAttribute1.value = this.editMode ? 'false' : 'true';
    subAttributeArray.push(subAttribute1);

    //Backend expecting udpPort value when stop reflector
    const subAttribute2 = {
      id: '',
      value: ''
    };
    subAttribute2.id = this.attributeMapping.twampUDPPort;
    subAttribute2.value = this.output[ConfigFieldMapping.UDP_PORT].toString();
    subAttributeArray.push(subAttribute2);

    // Backend expecting "no_filter_twamp_ignore_ip" field as mandatory for updating twamp reflector
    const subAttribute3 = {
      id: '',
      value: ''
    };
    subAttribute3.id = this.attributeMapping.twampIgnoreIp;
    subAttribute3.value = 'false';
    subAttributeArray.push(subAttribute3);

    const valueObj = {
      name: '',
      subAttributes: []
    };
    valueObj.name = this.attributeMapping.sectionName;
    valueObj.subAttributes = subAttributeArray;
    lmapOptionsArray['config-attributes'].option[0].value.data.push(valueObj);
    return lmapOptionsArray;
  }
}
